import { useEffect, useState } from "react";
import type { CncMachineProfile } from "../../../shared/api-contract";
import { Disclosure, Icon } from "../../kit";
import { uniqueProfileName, validateCncProfile } from "./cncProfile";
import type { CncProfileShelf } from "./cncProfile";
import { Banner, CheckRow, Choice, LabeledText, NumInput, SmallButton } from "./cncUi";

/** The Z-follow switch's element id: the `groove.shallow` check points the user at it. */
export const CNC_ZFOLLOW_INPUT_ID = "cnc-zfollow";

/**
 * The machine-profile half of the CNC dialog: which saved profile is in use,
 * a simple form for the fields a shop actually changes, the cutters at a
 * glance, and the whole profile as JSON for everything else.
 *
 * Every edit goes through `validateCncProfile` BEFORE it reaches the shelf, so
 * the profile the dialog exports with is always one that passed — a refused
 * edit stays on screen with its reason and the last good profile stays in use.
 */
export function CncProfileEditor({
  shelf,
  onShelfChange,
  loadDefault,
}: {
  shelf: CncProfileShelf;
  onShelfChange: (shelf: CncProfileShelf) => void;
  /** A fresh copy of the shop's sample profile (`getCncDefaultProfile`). */
  loadDefault: () => Promise<CncMachineProfile>;
}) {
  const profile = shelf.profiles.find((p) => p.name === shelf.selected) ?? shelf.profiles[0];
  const [formError, setFormError] = useState<string[] | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const [newName, setNewName] = useState<string | null>(null);

  useEffect(() => { setFormError(null); setConfirmReset(false); }, [shelf.selected]);

  /** Replace the selected profile with `next`, if it passes. Returns the first error, or null. */
  const commit = (next: CncMachineProfile): string | null => {
    const check = validateCncProfile(next);
    if (!check.ok) {
      setFormError(check.errors);
      return check.errors[0];
    }
    if (next.name !== profile.name && shelf.profiles.some((p) => p.name === next.name)) {
      const msg = `Another saved profile is already called "${next.name}".`;
      setFormError([msg]);
      return msg;
    }
    setFormError(null);
    onShelfChange({
      selected: next.name,
      profiles: shelf.profiles.map((p) => (p === profile ? next : p)),
    });
    return null;
  };

  const patch = (fn: (p: CncMachineProfile) => CncMachineProfile) => commit(fn(structuredClone(profile)));

  const onReset = async () => {
    if (!confirmReset) return setConfirmReset(true);
    setResetBusy(true);
    try {
      const fresh = await loadDefault();
      const check = validateCncProfile(fresh);
      if (!check.ok) return setFormError(["The viewer's sample profile did not pass the checks:", ...check.errors]);
      // Keep this slot's name unless it collides with ANOTHER saved profile.
      const others = { ...shelf, profiles: shelf.profiles.filter((p) => p !== profile) };
      const name = uniqueProfileName(fresh.name, others);
      onShelfChange({ selected: name, profiles: shelf.profiles.map((p) => (p === profile ? { ...fresh, name } : p)) });
      setFormError(null);
    } catch (e) {
      setFormError([`Could not load the sample profile: ${(e as Error).message || "unknown error"}`]);
    } finally {
      setResetBusy(false);
      setConfirmReset(false);
    }
  };

  const onSaveAs = () => {
    const base = (newName ?? "").trim();
    if (!base) return setFormError(["Give the new profile a name."]);
    const name = uniqueProfileName(base, shelf);
    onShelfChange({ selected: name, profiles: [...shelf.profiles, { ...structuredClone(profile), name }] });
    setNewName(null);
  };

  const onDelete = () => {
    if (shelf.profiles.length < 2) return;
    const rest = shelf.profiles.filter((p) => p !== profile);
    onShelfChange({ selected: rest[0].name, profiles: rest });
  };

  const shopTools = profile.tools.filter((t) => t.source === "shop");
  const assumedCount = profile.tools.length - shopTools.length;
  const canMarkCalibrated = !!profile.calibratedBy?.trim() && /^\d{4}-\d{2}-\d{2}/.test(profile.calibratedAt ?? "");

  return (
    <div className="flex flex-col gap-3" data-testid="cnc-profile">
      {/* ── Which profile ── */}
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex min-w-[180px] flex-1 flex-col gap-1">
          <span className="font-body text-[11px] font-medium text-ed-text-2">Profile in use</span>
          <select
            data-testid="cnc-profile-select"
            className="w-full rounded-md border border-ed-line/60 bg-ed-panel px-2.5 py-1.5 font-body text-[12px] text-ed-text outline-none focus:border-ed-accent"
            value={profile.name}
            onChange={(e) => onShelfChange({ ...shelf, selected: e.target.value })}
          >
            {shelf.profiles.map((p) => (
              <option key={p.name} value={p.name}>{p.name}{p.calibrated ? "" : " — not calibrated"}</option>
            ))}
          </select>
        </label>
        <SmallButton onClick={() => setNewName(newName === null ? `${profile.name} copy` : null)}>
          <Icon name="content_copy" size={14} /> Save a copy
        </SmallButton>
        <SmallButton tone="danger" disabled={shelf.profiles.length < 2} onClick={onDelete} title={shelf.profiles.length < 2 ? "The last profile cannot be deleted" : undefined}>
          Delete
        </SmallButton>
      </div>
      {newName !== null ? (
        <div className="flex items-end gap-2">
          <LabeledText label="Name of the copy" value={newName} onChange={setNewName} />
          <SmallButton tone="ink" onClick={onSaveAs}>Save</SmallButton>
        </div>
      ) : null}

      {formError ? (
        <Banner tone="fail" icon="error" testId="cnc-profile-error">
          <span className="font-semibold">That change was not applied.</span>
          <ul className="mt-1 list-disc pl-4">{formError.map((e) => <li key={e}>{e}</li>)}</ul>
        </Banner>
      ) : null}

      {/* ── Simple form ── */}
      <LabeledText label="Profile name" value={profile.name} onChange={(v) => { if (v.trim()) patch((p) => ({ ...p, name: v })); }} />

      <div className="flex flex-col gap-2 rounded-lg border border-ed-border bg-ed-panel-2 p-3">
        <span className="font-body text-[12px] font-semibold text-ed-text">Proven at the shop</span>
        <span className="font-body text-[11px] text-ed-text-3">
          Only mark this once the air cut, the direction test and a brass test piece have all come out right on this machine.
        </span>
        <div className="flex gap-2">
          <LabeledText label="Proven by" value={profile.calibratedBy ?? ""} onChange={(v) => patch((p) => ({ ...p, calibratedBy: v || undefined, ...(v.trim() ? {} : { calibrated: false }) }))} />
          <LabeledText label="Date (YYYY-MM-DD)" value={profile.calibratedAt ?? ""} onChange={(v) => patch((p) => ({ ...p, calibratedAt: v || undefined, ...(/^\d{4}-\d{2}-\d{2}/.test(v) ? {} : { calibrated: false }) }))} />
        </div>
        <CheckRow
          testId="cnc-profile-calibrated"
          checked={profile.calibrated}
          disabled={!profile.calibrated && !canMarkCalibrated}
          onChange={(v) => patch((p) => ({ ...p, calibrated: v }))}
          label="This profile has been proven on the machine"
          hint={!profile.calibrated && !canMarkCalibrated ? "Fill in who proved it and the date first." : undefined}
        />
      </div>

      <div className="grid grid-cols-2 gap-x-3 gap-y-2">
        <div className="flex flex-col gap-1">
          <span className="font-body text-[11px] font-medium text-ed-text-2">Rotation direction (C)</span>
          <Choice options={[{ id: 1, label: "+1" }, { id: -1, label: "−1" }]} value={profile.kinematics.signC} onChange={(v) => patch((p) => ({ ...p, kinematics: { ...p.kinematics, signC: v } }))} />
        </div>
        <div className="flex flex-col gap-1">
          <span className="font-body text-[11px] font-medium text-ed-text-2">Across-the-band direction (Y)</span>
          <Choice options={[{ id: 1, label: "+1" }, { id: -1, label: "−1" }]} value={profile.kinematics.signY} onChange={(v) => patch((p) => ({ ...p, kinematics: { ...p.kinematics, signY: v } }))} />
        </div>
        <span className="col-span-2 -mt-1 font-body text-[11px] text-ed-text-3">
          Directions are a guess until the direction test at the shop. A wrong one mirrors the cut.
        </span>
        <div className="col-span-2 flex flex-col gap-2 rounded-lg border border-ed-border p-3">
          <CheckRow
            id={CNC_ZFOLLOW_INPUT_ID}
            testId="cnc-profile-zfollow"
            checked={profile.kinematics.allowSimultaneousZ}
            onChange={(v) => patch((p) => ({ ...p, kinematics: { ...p.kinematics, allowSimultaneousZ: v } }))}
            label="Z-follow: the machine can move Z while it turns"
            hint="Leave off until the shop confirms its machine moves Z together with Y and C on one line (questionnaire Q23). On, grooves and channels follow a domed band more closely, but never deeper than designed: a wide cutter on a slope still comes out a little shallower, and the rounded edge is always held level. Some designs then fail the wall check."
          />
          {profile.kinematics.allowSimultaneousZ ? (
            <Banner tone="warn" icon="warning" testId="cnc-profile-zfollow-on">
              <span className="font-semibold">Z-follow is on.</span> The shop&rsquo;s own program never moves Z with Y or C, so these
              moves are unproven on its machine: run the air-cut file in single-block and watch every groove before any metal.
            </Banner>
          ) : null}
        </div>
        <NumInput label="Least metal under a cut" unit="mm" value={profile.limits.minWallMM} onCommit={(v) => patch((p) => ({ ...p, limits: { ...p.limits, minWallMM: v } }))} />
        <NumInput label="Least metal between seats" unit="mm" value={profile.limits.minWebMM} onCommit={(v) => patch((p) => ({ ...p, limits: { ...p.limits, minWebMM: v } }))} />
        <NumInput label="Air-cut lift" unit="mm" value={profile.airCutOffsetMM} onCommit={(v) => patch((p) => ({ ...p, airCutOffsetMM: v }))} hint="How far the air-cut file stays above the part." />
        <NumInput label="Largest turn in one move" unit="°" value={profile.kinematics.maxCPerBlockDeg} onCommit={(v) => patch((p) => ({ ...p, kinematics: { ...p.kinematics, maxCPerBlockDeg: v } }))} />
        <NumInput label="Approach above last floor" unit="mm" value={profile.motion.approachMM} onCommit={(v) => patch((p) => ({ ...p, motion: { ...p.motion, approachMM: v } }))} />
        <NumInput label="Lift after each pass" unit="mm" value={profile.motion.liftMM} onCommit={(v) => patch((p) => ({ ...p, motion: { ...p.motion, liftMM: v } }))} />
      </div>

      {/* ── Cutters at a glance ── */}
      <div className="flex flex-col gap-1.5">
        <span className="font-body text-[11px] font-medium text-ed-text-2">The shop&rsquo;s cutters</span>
        <div className="overflow-hidden rounded-lg border border-ed-border">
          <table className="w-full font-body text-[11px] text-ed-text">
            <thead className="bg-ed-panel-2 text-left text-ed-text-2">
              <tr><th className="px-2 py-1 font-medium">Tool</th><th className="px-2 py-1 font-medium">Offset</th><th className="px-2 py-1 font-medium">Ø</th><th className="px-2 py-1 font-medium">Name in the program</th></tr>
            </thead>
            <tbody>
              {shopTools.map((t) => (
                <tr key={t.id} className="border-t border-ed-border">
                  <td className="px-2 py-1 tabular-nums">T{t.t}</td>
                  <td className="px-2 py-1">{t.wcs}</td>
                  <td className="px-2 py-1 tabular-nums">{t.diameterMM} mm</td>
                  <td className="px-2 py-1 font-mono">{t.label}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {assumedCount > 0 ? (
          <span className="font-body text-[11px] text-ed-text-3">
            Plus {assumedCount} cutters the shop has not confirmed it owns. A program that needs one says so in its checks.
          </span>
        ) : null}
      </div>

      <AdvancedJson profile={profile} commit={commit} />

      <div className="flex flex-wrap items-center gap-2">
        <SmallButton testId="cnc-profile-reset" tone={confirmReset ? "danger" : "plain"} disabled={resetBusy} onClick={() => void onReset()}>
          <Icon name="restart_alt" size={14} />
          {resetBusy ? "Loading…" : confirmReset ? "Click again to replace this profile" : "Reset to the shop's sample profile"}
        </SmallButton>
        {confirmReset ? <SmallButton onClick={() => setConfirmReset(false)}>Cancel</SmallButton> : null}
      </div>
    </div>
  );
}

/** The whole profile as JSON. Checked in full before it replaces anything. */
function AdvancedJson({ profile, commit }: { profile: CncMachineProfile; commit: (p: CncMachineProfile) => string | null }) {
  const pretty = JSON.stringify(profile, null, 2);
  const [text, setText] = useState(pretty);
  const [dirty, setDirty] = useState(false);
  const [errors, setErrors] = useState<string[] | null>(null);

  // Follow the form while the JSON has no unsaved edits of its own.
  useEffect(() => { if (!dirty) setText(pretty); }, [pretty, dirty]);

  const apply = () => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch (e) {
      return setErrors([`This is not valid JSON: ${(e as Error).message}`]);
    }
    const check = validateCncProfile(parsed);
    if (!check.ok) return setErrors(check.errors);
    const err = commit(check.profile);
    if (err) return setErrors([err]);
    setErrors(null);
    setDirty(false);
  };

  return (
    <Disclosure label="Advanced (JSON)" value={dirty ? "unsaved changes" : undefined}>
      <span className="font-body text-[11px] text-ed-text-3">
        Every setting: the program&rsquo;s fixed lines, number format, cutters, feeds and the seat table. Checked in full before it is used.
      </span>
      <textarea
        data-testid="cnc-profile-json"
        className="scroll-thin h-72 w-full resize-y rounded-md border border-ed-line/60 bg-ed-panel p-2 font-mono text-[11px] leading-snug text-ed-text outline-none focus:border-ed-accent"
        spellCheck={false}
        value={text}
        onChange={(e) => { setText(e.target.value); setDirty(true); }}
      />
      {errors ? (
        <Banner tone="fail" icon="error" testId="cnc-profile-json-error">
          <span className="font-semibold">Not used — fix these first:</span>
          <ul className="mt-1 list-disc pl-4">{errors.slice(0, 12).map((e) => <li key={e}>{e}</li>)}</ul>
          {errors.length > 12 ? <span>…and {errors.length - 12} more.</span> : null}
        </Banner>
      ) : null}
      <div className="flex gap-2">
        <SmallButton tone="ink" disabled={!dirty} onClick={apply} testId="cnc-profile-json-apply">Use this JSON</SmallButton>
        <SmallButton disabled={!dirty} onClick={() => { setText(pretty); setDirty(false); setErrors(null); }}>Discard changes</SmallButton>
      </div>
    </Disclosure>
  );
}
